import { useCallback, useRef, useEffect } from 'react';
import * as CONSTANTS from '../shared/constants';

const {
  GRID_SIZE,
  TILE_SIZE,
  CENTER_TOLERANCE,
  NETWORK_UPDATE_INTERVAL,
  SNAP_THRESHOLD,
  ANIMATION_BASE_INTERVAL,
  DEFAULT_SPEED,
  PLAYER_RADIUS_X,
  PLAYER_RADIUS_Y
} = CONSTANTS;

const usePlayerMovement = ({
  myPlayer,
  myPlayerId,
  gameState,
  isObserver,
  bombs,
  walls,
  setPlayers,
  emitPlayerInput,
  emitKickBomb,
  playerAnimationState
}) => {
  // Network throttling refs
  const lastNetworkUpdate = useRef(0);
  const pendingNetworkUpdate = useRef(null);
  const keysPressed = useRef({});
  
  // Track which bombs the player was overlapping with when they were placed
  // This allows any player who was inside the bomb when it was placed to walk out
  // Prevents the "getting stuck inside a bomb" bug when players turn back before fully clearing
  const overlappingBombs = useRef(new Set());

  // Optimized buffer search using binary search for better performance
  const findInterpolationStates = useCallback((buffer, renderTime) => {
    if (buffer.length === 0) return { older: null, newer: null };
    if (buffer.length === 1) return { older: buffer[0], newer: null };
    
    // Binary search for the bracketing states
    let left = 0;
    let right = buffer.length - 1;
    
    // If renderTime is before first or after last, handle edge cases
    if (renderTime <= buffer[0].time) {
      return { older: buffer[0], newer: buffer[1] };
    }
    if (renderTime >= buffer[right].time) {
      return { older: buffer[right - 1], newer: buffer[right] };
    }
    
    // Binary search for bracketing states
    while (left < right - 1) {
      const mid = Math.floor((left + right) / 2);
      if (buffer[mid].time <= renderTime) {
        left = mid;
      } else {
        right = mid;
      }
    }
    
    return { older: buffer[left], newer: buffer[right] };
  }, []);

  // Robust interpolation function that handles all edge cases
  const interpolatePosition = useCallback((buffer, renderTime, now, isPixelCoords = false) => {
    if (!buffer || buffer.length === 0) return null;
    
    const { older, newer } = findInterpolationStates(buffer, renderTime);
    
    if (!older && !newer) return null;
    
    // Single point or no newer point - try extrapolation
    if (!newer) {
      if (buffer.length >= 2) {
        const last = buffer[buffer.length - 1];
        const secondLast = buffer[buffer.length - 2];
        
        // Only extrapolate if we're reasonably close to the last update (within 200ms)
        if (now - last.time < 200) {
          const deltaTime = last.time - secondLast.time;
          if (deltaTime > 0) {
            const extrapolationTime = (renderTime - last.time);
            if (isPixelCoords) {
              const deltaX = last.pixelX - secondLast.pixelX;
              const deltaY = last.pixelY - secondLast.pixelY;
              const velocityX = deltaX / deltaTime;
              const velocityY = deltaY / deltaTime;
              return {
                x: last.pixelX + velocityX * extrapolationTime,
                y: last.pixelY + velocityY * extrapolationTime
              };
            } else {
              const deltaX = last.x - secondLast.x;
              const deltaY = last.y - secondLast.y;
              const velocityX = deltaX / deltaTime;
              const velocityY = deltaY / deltaTime;
              return {
                x: last.x + velocityX * extrapolationTime,
                y: last.y + velocityY * extrapolationTime
              };
            }
          }
        }
        // Fallback to most recent position
        return isPixelCoords ? 
          { x: buffer[buffer.length - 1].pixelX, y: buffer[buffer.length - 1].pixelY } :
          { x: buffer[buffer.length - 1].x, y: buffer[buffer.length - 1].y };
      } else {
        // Single point in buffer
        return isPixelCoords ? 
          { x: buffer[0].pixelX, y: buffer[0].pixelY } :
          { x: buffer[0].x, y: buffer[0].y };
      }
    }
    
    // We have bracketing states, interpolate normally
    const timeDelta = newer.time - older.time;
    if (timeDelta <= 0) {
      // Same timestamp, use newer position
      return isPixelCoords ? 
        { x: newer.pixelX, y: newer.pixelY } :
        { x: newer.x, y: newer.y };
    }
    
    const t = Math.max(0, Math.min(1, (renderTime - older.time) / timeDelta));
    
    // Check if this is a small movement (smooth) or large teleport (snap)
    const distance = isPixelCoords ?
      Math.sqrt((newer.pixelX - older.pixelX) ** 2 + (newer.pixelY - older.pixelY) ** 2) :
      Math.sqrt((newer.x - older.x) ** 2 + (newer.y - older.y) ** 2);
    
    if (distance <= SNAP_THRESHOLD) {
      // Small movement - interpolate smoothly
      if (isPixelCoords) {
        return {
          x: older.pixelX + (newer.pixelX - older.pixelX) * t,
          y: older.pixelY + (newer.pixelY - older.pixelY) * t
        };
      } else {
        return {
          x: older.x + (newer.x - older.x) * t,
          y: older.y + (newer.y - older.y) * t
        };
      }
    } else {
      // Large movement - snap to newer position
      return isPixelCoords ? 
        { x: newer.pixelX, y: newer.pixelY } :
        { x: newer.x, y: newer.y };
    }
  }, [findInterpolationStates]);

  // Collision detection functions
  const isBombAt = useCallback((centerX, centerY, radiusX = PLAYER_RADIUS_X, radiusY = PLAYER_RADIUS_Y, checkOnly = false) => {
    return bombs.find(bomb => {
      const bombCenterX = bomb.x * GRID_SIZE + GRID_SIZE / 2;
      const bombCenterY = bomb.y * GRID_SIZE + GRID_SIZE / 2;
      
      // Ellipse-to-circle collision (treating bomb as circle with radius GRID_SIZE/2 - 2)
      const bombRadius = GRID_SIZE / 2 - 2;
      const dx = centerX - bombCenterX;
      const dy = centerY - bombCenterY;
      
      // Distance from ellipse center to bomb center
      const distance = Math.sqrt(dx ** 2 + dy ** 2);
      
      // Simple approximation: use average of ellipse radii for collision with bomb
      const avgPlayerRadius = (radiusX + radiusY) / 2;
      
      const isOverlapping = distance < avgPlayerRadius + bombRadius;
      
      // Don't check collision rules if we're just checking for overlap
      if (checkOnly) {
        return isOverlapping;
      }
      
      // Allow pass-through if:
      // 1. Bomb is still safe (server hasn't marked it solid yet), OR
      // 2. Player was overlapping with the bomb when it was placed and hasn't cleared it yet
      // This prevents the "getting stuck inside a bomb" bug where players can't escape
      // if they turn back before fully clearing a bomb they were inside of
      const wasInitiallyOverlapping = overlappingBombs.current.has(bomb.id);
      const canPassThrough = bomb.safe || wasInitiallyOverlapping;
      
      return isOverlapping && !canPassThrough;
    });
  }, [bombs]);

  const isWallAtGrid = useCallback((gridX, gridY) => {
    return walls.some(wall => wall.x === gridX && wall.y === gridY);
  }, [walls]);

  const checkEllipticalCollision = useCallback((centerX, centerY, radiusX = PLAYER_RADIUS_X, radiusY = PLAYER_RADIUS_Y) => {
    const left = centerX - radiusX;
    const right = centerX + radiusX;
    const top = centerY - radiusY;
    const bottom = centerY + radiusY;
    
    const leftGrid = Math.floor(left / GRID_SIZE);
    const rightGrid = Math.floor(right / GRID_SIZE);
    const topGrid = Math.floor(top / GRID_SIZE);
    const bottomGrid = Math.floor(bottom / GRID_SIZE);
    
    for (let gx = leftGrid; gx <= rightGrid; gx++) {
      for (let gy = topGrid; gy <= bottomGrid; gy++) {
        if (gx < 0 || gx >= CONSTANTS.GRID_WIDTH || gy < 0 || gy >= CONSTANTS.GRID_HEIGHT || isWallAtGrid(gx, gy)) {
          const closestX = Math.max(gx * GRID_SIZE, Math.min(centerX, (gx + 1) * GRID_SIZE));
          const closestY = Math.max(gy * GRID_SIZE, Math.min(centerY, (gy + 1) * GRID_SIZE));
          
          // Ellipse collision check: (dx/rx)² + (dy/ry)² < 1
          const dx = centerX - closestX;
          const dy = centerY - closestY;
          const ellipseTest = (dx / radiusX) ** 2 + (dy / radiusY) ** 2;
          
          if (ellipseTest < 1) {
            return true;
          }
        }
      }
    }
    return false;
  }, [isWallAtGrid]);

  // Helper functions for smooth movement algorithm
  const getTileCenter = useCallback((tileX, tileY) => {
    return {
      x: tileX * TILE_SIZE + TILE_SIZE / 2,
      y: tileY * TILE_SIZE + TILE_SIZE / 2
    };
  }, []);

  const getTileCoords = useCallback((px, py) => {
    return {
      tx: Math.floor(px / TILE_SIZE),
      ty: Math.floor(py / TILE_SIZE)
    };
  }, []);

  const willCollideSmooth = useCallback((testPX, testPY) => {
    return checkEllipticalCollision(testPX, testPY);
  }, [checkEllipticalCollision]);

  // Network update is now handled by input sending
  const sendNetworkUpdate = useCallback(() => {
    // Deprecated - inputs are sent periodically instead
  }, []);

  // Track new bombs and clean up removed bombs
  // Part of the fix for "getting stuck inside a bomb" bug
  useEffect(() => {
    if (!myPlayer) return;
    
    const currentBombIds = new Set(bombs.map(b => b.id));
    const trackedBombIds = Array.from(overlappingBombs.current);
    
    // Remove tracked bomb IDs that no longer exist
    trackedBombIds.forEach(id => {
      if (!currentBombIds.has(id)) {
        overlappingBombs.current.delete(id);
      }
    });
    
    // Check if player is overlapping with any new bombs when they appear
    // This handles cases where multiple players are on the same tile when a bomb is placed
    const playerCenterX = myPlayer.x + GRID_SIZE / 2;
    const playerCenterY = myPlayer.y + GRID_SIZE / 2;
    
    bombs.forEach(bomb => {
      // If we're not already tracking this bomb, check if we're overlapping
      if (!overlappingBombs.current.has(bomb.id)) {
        const bombCenterX = bomb.x * GRID_SIZE + GRID_SIZE / 2;
        const bombCenterY = bomb.y * GRID_SIZE + GRID_SIZE / 2;
        const dx = playerCenterX - bombCenterX;
        const dy = playerCenterY - bombCenterY;
        const distance = Math.sqrt(dx ** 2 + dy ** 2);
        const avgPlayerRadius = (PLAYER_RADIUS_X + PLAYER_RADIUS_Y) / 2;
        const bombRadius = GRID_SIZE / 2 - 2;
        
        // If we're overlapping with this new bomb, track it
        if (distance < avgPlayerRadius + bombRadius) {
          overlappingBombs.current.add(bomb.id);
        }
      }
    });
  }, [bombs, myPlayer]);

  const movePlayer = useCallback((inputX, inputY) => {
    if (!myPlayer || !myPlayer.alive || gameState !== 'playing' || isObserver) return;

    // Get current animation state (direction is now set in gameLoop)
    const animState = playerAnimationState.current.get(myPlayerId) || { direction: 'front', frame: 0, lastMoveTime: 0, lastFrameChangeTime: 0 };
    const now = Date.now();
    
    // Animate walking frames (cycle through 1-3, 0 is standing)
    if (inputX !== 0 || inputY !== 0) {
      // Animation speed based on player speed (faster movement = faster animation)
      const speed = myPlayer.speed || DEFAULT_SPEED;
      const baseAnimSpeed = ANIMATION_BASE_INTERVAL; // Base animation interval in ms
      const speedMultiplier = DEFAULT_SPEED / speed; // Inverse: higher speed = lower interval
      const animInterval = baseAnimSpeed * speedMultiplier * 0.5; // Half the interval for double speed baseline
      
      if (now - animState.lastMoveTime > animInterval) {
        animState.frame = (animState.frame % 3) + 1; // Cycle 1->2->3->1
        animState.lastMoveTime = now;
        animState.lastFrameChangeTime = now;
        playerAnimationState.current.set(myPlayerId, animState);
      }
    }

    // Convert current player position to center coordinates
    let px = myPlayer.x + GRID_SIZE / 2;
    let py = myPlayer.y + GRID_SIZE / 2;
    const speed = myPlayer.speed || DEFAULT_SPEED;

    // Get current tile and center
    let { tx, ty } = getTileCoords(px, py);
    let center = getTileCenter(tx, ty);

    // Auto-align on perpendicular axis when moving vertically
    if (inputY !== 0) {
      let dx = center.x - px;
      if (Math.abs(dx) <= CENTER_TOLERANCE) {
        // Snap toward center
        px += Math.sign(dx) * Math.min(Math.abs(dx), speed);
      } else {
        // If not within tolerance, force move sideways toward alignment
        px += Math.sign(dx) * speed;
        
        // Convert back to top-left corner coordinates
        const newX = px - GRID_SIZE / 2;
        const newY = py - GRID_SIZE / 2;
        
        // Update player position
        setPlayers(prev => {
          const newPlayers = new Map(prev);
          const player = newPlayers.get(myPlayerId);
          if (player) {
            player.x = newX;
            player.y = newY;
          }
          return newPlayers;
        });
        
        sendNetworkUpdate(newX, newY);
        return; // Don't move vertically until aligned
      }
    }

    // Auto-align on perpendicular axis when moving horizontally
    if (inputX !== 0) {
      let dy = center.y - py;
      if (Math.abs(dy) <= CENTER_TOLERANCE) {
        py += Math.sign(dy) * Math.min(Math.abs(dy), speed);
      } else {
        py += Math.sign(dy) * speed;
        
        // Convert back to top-left corner coordinates
        const newX = px - GRID_SIZE / 2;
        const newY = py - GRID_SIZE / 2;
        
        // Update player position
        setPlayers(prev => {
          const newPlayers = new Map(prev);
          const player = newPlayers.get(myPlayerId);
          if (player) {
            player.x = newX;
            player.y = newY;
          }
          return newPlayers;
        });
        
        sendNetworkUpdate(newX, newY);
        return; // Don't move horizontally until aligned
      }
    }

    // Apply movement if next position is not blocked
    let nextPX = px + inputX * speed;
    let nextPY = py + inputY * speed;

    // Check for bomb collision and handle kicking
    const bombHit = isBombAt(nextPX, nextPY);
    if (bombHit && !bombHit.moving) {
      const direction = {
        x: Math.sign(inputX) || 0,
        y: Math.sign(inputY) || 0
      };
      if (direction.x !== 0 || direction.y !== 0) {
        emitKickBomb({ bombId: bombHit.id, direction });
        return;
      }
    }

    // Check collision with walls using smaller hitbox
    if (!willCollideSmooth(nextPX, nextPY) && !bombHit) {
      px = nextPX;
      py = nextPY;
    }

    // Convert back to top-left corner coordinates
    const finalX = px - GRID_SIZE / 2;
    const finalY = py - GRID_SIZE / 2;

    // Check if player has cleared any bombs they were initially overlapping with
    // Once cleared, the bomb becomes solid to prevent walking back through it
    // Part of the fix for "getting stuck inside a bomb" bug
    bombs.forEach(bomb => {
      if (overlappingBombs.current.has(bomb.id)) {
        // Check if player is still overlapping with the bomb
        const bombCenterX = bomb.x * GRID_SIZE + GRID_SIZE / 2;
        const bombCenterY = bomb.y * GRID_SIZE + GRID_SIZE / 2;
        const dx = px - bombCenterX;
        const dy = py - bombCenterY;
        const distance = Math.sqrt(dx ** 2 + dy ** 2);
        const avgPlayerRadius = (PLAYER_RADIUS_X + PLAYER_RADIUS_Y) / 2;
        const bombRadius = GRID_SIZE / 2 - 2;
        
        // If no longer overlapping, remove from tracking so bomb becomes solid
        if (distance >= avgPlayerRadius + bombRadius) {
          overlappingBombs.current.delete(bomb.id);
        }
      }
    });

    // Only update if position changed
    if (finalX !== myPlayer.x || finalY !== myPlayer.y) {
      // Update local player immediately for smooth movement
      setPlayers(prev => {
        const newPlayers = new Map(prev);
        const player = newPlayers.get(myPlayerId);
        if (player) {
          player.x = finalX;
          player.y = finalY;
        }
        return newPlayers;
      });
      
      sendNetworkUpdate(finalX, finalY);
    }
  }, [myPlayer, gameState, isObserver, getTileCoords, getTileCenter, willCollideSmooth, isBombAt, myPlayerId, sendNetworkUpdate, emitKickBomb, setPlayers, playerAnimationState, bombs]);

  // Send inputs to server - much simpler with server-authoritative movement
  const INPUT_SEND_RATE = 50; // Send inputs 20 times per second

  useEffect(() => {
    let inputIntervalId;
    
    inputIntervalId = setInterval(() => {
      if (gameState === 'playing' && myPlayer?.alive) {
        // Check if player has confusion disease (reverses controls)
        const hasConfusion = myPlayer?.diseases && 
                             myPlayer.diseases.confusion && 
                             Date.now() < myPlayer.diseases.confusion;
        
        // Send current input state to server (with confusion disease reversal)
        emitPlayerInput({
          up: hasConfusion ? (keysPressed.current['ArrowDown'] || false) : (keysPressed.current['ArrowUp'] || false),
          down: hasConfusion ? (keysPressed.current['ArrowUp'] || false) : (keysPressed.current['ArrowDown'] || false),
          left: hasConfusion ? (keysPressed.current['ArrowRight'] || false) : (keysPressed.current['ArrowLeft'] || false),
          right: hasConfusion ? (keysPressed.current['ArrowLeft'] || false) : (keysPressed.current['ArrowRight'] || false),
          bomb: keysPressed.current[' '] || false
        });
      }
    }, INPUT_SEND_RATE);
    
    return () => {
      if (inputIntervalId) {
        clearInterval(inputIntervalId);
      }
    };
  }, [emitPlayerInput, gameState, myPlayer]);

  // Keyboard event handlers
  const handleKeyDown = useCallback((e) => {      
    if (gameState === 'playing' && myPlayer?.alive && !isObserver) {
      keysPressed.current[e.key] = true;
    }
  }, [gameState, myPlayer, isObserver]);

  const handleKeyUp = useCallback((e) => {
    if (!isObserver) {
      keysPressed.current[e.key] = false;
    }
  }, [isObserver]);

  return {
    findInterpolationStates,
    interpolatePosition,
    isBombAt,
    isWallAtGrid,
    checkEllipticalCollision,
    getTileCenter,
    getTileCoords,
    willCollideSmooth,
    sendNetworkUpdate,
    movePlayer,
    handleKeyDown,
    handleKeyUp,
    keysPressed
  };
};

export default usePlayerMovement;